// ═══════════════════════════════════════════════════════════════════════════
// Supabase Client Configuration
// ═══════════════════════════════════════════════════════════════════════════

import { createClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// AUTH LOCK, per-tab serializing mutex (deliberately NOT the cross-tab navigatorLock).
//
// History of this line, so it is not "fixed" in the wrong direction again:
//  1. Original custom lock: re-entered immediately when held, so it did NOT
//     serialize, two refreshes in one tab could race.
//  2. navigatorLock (Web Locks API, cross-tab): serializes across ALL tabs of
//     the origin. The fatal downside for a multi-tab admin: if ONE tab wedges
//     while refreshing a bad token, it holds the shared lock and EVERY other
//     tab blocks on session resolution, so the whole app sits on "Loading LWD"
//     and never boots. That is the incident this comment exists to prevent.
//  3. THIS: a proper per-tab async mutex. Each tab has its own lock, so a
//     wedged tab can only ever stall itself, it can never freeze the others.
//     Within a tab it strictly serializes (no refresh race). auth-js (>=2)
//     handles lock RE-ENTRY itself via lockAcquired + pendingInLock and only
//     calls this.lock() for top-level acquisitions, so a non-re-entrant
//     serializing mutex is correct here. A safety timeout releases the lock if
//     a critical section ever stalls, so a single tab self-heals too.
const _lockTail = {};
async function processLock(name, acquireTimeout, fn) {
  const prev = _lockTail[name] || Promise.resolve();
  let release;
  const current = new Promise((resolve) => { release = resolve; });
  // Append to the chain synchronously (before any await) for deterministic order.
  _lockTail[name] = prev.then(() => current);
  await prev.catch(() => {}); // a prior rejection must not break the chain

  // Never hold longer than auth-js's acquire timeout (5s). If a refresh stalls,
  // release so this tab can retry, other tabs are unaffected (per-tab lock).
  const ceiling = (typeof acquireTimeout === 'number' && acquireTimeout > 0) ? acquireTimeout : 5000;
  let timer;
  try {
    return await Promise.race([
      fn(),
      new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('auth lock timeout')), ceiling); }),
    ]);
  } finally {
    clearTimeout(timer);
    release();
  }
}

let supabase = null;

// Session-free client for public/discovery queries.
// Uses anon-key only, no session JWT attached, so the anon RLS policies
// apply (listings_public_select_published etc.). Use this for any query that
// should behave the same whether the admin is logged in or not.
let supabaseAnon = null;

if (supabaseUrl && supabaseAnonKey) {
  // Single real-session client, with the per-tab serializing lock above.
  supabase = createClient(supabaseUrl, supabaseAnonKey, {
    auth: { lock: processLock },
  });

  // Read-only anon client. persistSession:false → in-memory storage, so it does
  // not touch the real token. A DISTINCT storageKey also stops auth-js logging
  // "Multiple GoTrueClient instances detected ... under the same storage key".
  supabaseAnon = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession:       false,
      autoRefreshToken:     false,
      detectSessionInUrl:   false,
      storageKey:           'sb-lwd-anon-readonly',
    },
  });
} else if (import.meta.env.DEV) {
  console.warn("[Supabase] Environment variables not configured. Database features disabled.");
}

export { supabase, supabaseAnon };

export function isSupabaseAvailable() {
  return !!(supabaseUrl && supabaseAnonKey && supabase);
}
